// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, Input, Select, Skeleton } from '@/components/ui'
import {
  useIncomeStatement,
  useBalanceSheet,
  useCashFlow,
} from '@/hooks/useAccounting'
import { useProperties } from '@/hooks/useProperties'
import { useSelectedPropertyStore } from '@/stores'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { useGlAccountLabel } from '@/hooks/useGlAccountLabel'
import { localToday } from '@/lib/date'
import { api } from '@/lib/api'
import type { StatementAccountRow, CashFlowItem } from '@/types'
import { formatBaht } from '@/lib/money'

type Tab = 'income_statement' | 'balance_sheet' | 'cash_flow'

function StatementRow({
  label,
  amount,
  isTotal = false,
  indent = false,
}: {
  label: string
  amount: number
  isTotal?: boolean
  indent?: boolean
}) {
  return (
    <tr className={isTotal ? 'font-semibold border-t' : 'hover'}>
      <td className={indent ? 'pl-8' : ''}>
        <span className={isTotal ? 'text-base' : 'text-sm'}>{label}</span>
      </td>
      <td className="text-right tabular-nums">
        <span className={isTotal ? 'text-base' : 'text-sm'}>{formatBaht(amount, { locale: 'en', decimals: 2 })}</span>
      </td>
    </tr>
  )
}

function AccountRows({ rows }: { rows: StatementAccountRow[] }) {
  const accountLabel = useGlAccountLabel()
  return (
    <>
      {rows.map((row) => (
        <StatementRow
          key={row.code}
          label={`${row.code} — ${accountLabel(row.code, row.system, row.name)}`}
          amount={row.balance}
          indent
        />
      ))}
    </>
  )
}

function CashFlowRows({ items }: { items: CashFlowItem[] }) {
  const accountLabel = useGlAccountLabel()
  return (
    <>
      {items.map((item, idx) => (
        <StatementRow
          key={`${item.code}-${idx}`}
          label={`${item.code} — ${accountLabel(item.code, item.system, item.name)}`}
          amount={item.amount}
          indent
        />
      ))}
    </>
  )
}

export function FinancialStatementsPage() {
  const { t } = useTranslation('accounting')
  const { t: tc } = useTranslation('common')

  const [activeTab, setActiveTab] = useState<Tab>('income_statement')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)

  const { timezone } = useLocalizationConfig()

  // Dates for income statement & cash flow
  const today = localToday(timezone)
  const firstOfMonth = `${today.slice(0, 7)}-01`
  const [startDate, setStartDate] = useState(firstOfMonth)
  const [endDate, setEndDate] = useState(today)

  // Date for balance sheet
  const [asOfDate, setAsOfDate] = useState(today)

  const { data: propertiesData } = useProperties()
  const properties = propertiesData?.data || []

  const propertyOptions = [
    { value: '', label: t('filters.all_properties') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const commonParams = selectedPropertyId ? { property_id: selectedPropertyId } : {}

  const {
    data: incomeStatement,
    isLoading: isLoadingIS,
    isFetching: isFetchingIS,
  } = useIncomeStatement({
    start_date: startDate,
    end_date: endDate,
    ...commonParams,
  })

  const {
    data: balanceSheet,
    isLoading: isLoadingBS,
    isFetching: isFetchingBS,
  } = useBalanceSheet({
    as_of_date: asOfDate,
    ...commonParams,
  })

  const {
    data: cashFlow,
    isLoading: isLoadingCF,
    isFetching: isFetchingCF,
  } = useCashFlow({
    start_date: startDate,
    end_date: endDate,
    ...commonParams,
  })

  const isLoading = isLoadingIS || isLoadingBS || isLoadingCF
  const isFetching = isFetchingIS || isFetchingBS || isFetchingCF

  const handleExportCSV = async (type: string) => {
    let url = ''
    let params: Record<string, string> = {}

    if (type === 'income_statement') {
      url = '/api/financial_statements/income_statement'
      params = { start_date: startDate, end_date: endDate, format: 'csv' }
    } else if (type === 'balance_sheet') {
      url = '/api/financial_statements/balance_sheet'
      params = { as_of_date: asOfDate, format: 'csv' }
    } else {
      url = '/api/financial_statements/cash_flow'
      params = { start_date: startDate, end_date: endDate, format: 'csv' }
    }

    if (selectedPropertyId) params.property_id = String(selectedPropertyId)

    const response = await api.get(url, { params, responseType: 'blob' })
    const blob = new Blob([response.data], { type: 'text/csv' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `${type}_${localToday(timezone)}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  const tabOptions = [
    { value: 'income_statement', label: t('financial_statements.income_statement') },
    { value: 'balance_sheet', label: t('financial_statements.balance_sheet') },
    { value: 'cash_flow', label: t('financial_statements.cash_flow') },
  ]

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('financial_statements.title') }]} />

      <PageHeader
        title={t('financial_statements.title')}
        description={t('financial_statements.description')}
        isRefreshing={isFetching && !isLoading}
        // Property scope sits top-right (TC-MUI-A5 titleAction, mobile-only; the
        // desktop picker lives in the filter card below). The header actions are
        // [ ตัวเลือก (statement type) ][ ส่งออก CSV (primary) ]: the statement
        // switch (income statement / balance sheet / cash flow) lives in the
        // Options (ตัวเลือก) overflow — a sheet on mobile, inline on desktop —
        // with the current one checkmarked; Export is the solid primary.
        titleAction={
          <Select
            data-testid="page-property-picker-mobile"
            aria-label={tc('labels.select_property')}
            size="sm"
            value={selectedPropertyId ? String(selectedPropertyId) : ''}
            onChange={(e) => setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)}
            options={propertyOptions}
          />
        }
        primaryAction={{
          key: 'export',
          label: t('financial_statements.export_csv'),
          icon: Download,
          onClick: () => handleExportCSV(activeTab),
        }}
        overflowActions={tabOptions.map((opt) => ({
          key: `statement-${opt.value}`,
          label: opt.value === activeTab ? `✓ ${opt.label}` : opt.label,
          onClick: () => setActiveTab(opt.value as Tab),
        }))}
      />

      {/* Filters (ตัวกรอง) — dates only now; property is in the header ตัวเลือก and
          Export is the header primary. Collapsible on mobile. */}
      <Card>
        <button
          type="button"
          className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((o) => !o)}
        >
          <span>{tc('filters.toggle')}</span>
          <span className={`text-base-content/60 transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`} aria-hidden="true">▾</span>
        </button>
        <div className={`flex-wrap gap-4 items-end mt-3 sm:mt-0 ${filtersOpen ? 'flex' : 'hidden sm:flex'}`}>
          {activeTab !== 'balance_sheet' ? (
            <>
              <Input
                label={t('financial_statements.start_date')}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Input
                label={t('financial_statements.end_date')}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </>
          ) : (
            <Input
              label={t('financial_statements.as_of_date')}
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
            />
          )}
        </div>
      </Card>

      {/* Income Statement */}
      {activeTab === 'income_statement' && (
        <Card>
          {isLoadingIS ? (
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          ) : incomeStatement ? (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <tbody>
                  {/* Revenue */}
                  <tr>
                    <td colSpan={2} className="font-bold text-base pt-4">
                      {t('financial_statements.revenue')}
                    </td>
                  </tr>
                  <AccountRows rows={incomeStatement.revenue} />
                  <StatementRow
                    label={`${t('financial_statements.subtotal')} — ${t('financial_statements.revenue')}`}
                    amount={incomeStatement.total_revenue}
                    isTotal
                  />

                  {/* Expenses */}
                  <tr>
                    <td colSpan={2} className="font-bold text-base pt-4">
                      {t('financial_statements.expenses')}
                    </td>
                  </tr>
                  <AccountRows rows={incomeStatement.expenses} />
                  <StatementRow
                    label={`${t('financial_statements.subtotal')} — ${t('financial_statements.expenses')}`}
                    amount={incomeStatement.total_expenses}
                    isTotal
                  />

                  {/* Net Income */}
                  <tr className="bg-base-200">
                    <td className="font-bold text-base py-3">
                      {t('financial_statements.net_income')}
                    </td>
                    <td className="text-right font-bold text-base tabular-nums py-3">
                      <span
                        className={incomeStatement.net_income >= 0 ? 'text-success' : 'text-error'}
                      >
                        {formatBaht(incomeStatement.net_income, { locale: 'en', decimals: 2 })}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-base-content/60 text-sm text-center py-8">
              {t('financial_statements.empty_description')}
            </p>
          )}
        </Card>
      )}

      {/* Balance Sheet */}
      {activeTab === 'balance_sheet' && (
        <Card>
          {isLoadingBS ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          ) : balanceSheet ? (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <tbody>
                  {/* Assets */}
                  <tr>
                    <td colSpan={2} className="font-bold text-base pt-4">
                      {t('financial_statements.assets')}
                    </td>
                  </tr>
                  <AccountRows rows={balanceSheet.assets} />
                  <StatementRow
                    label={t('financial_statements.total_assets')}
                    amount={balanceSheet.total_assets}
                    isTotal
                  />

                  {/* Liabilities */}
                  <tr>
                    <td colSpan={2} className="font-bold text-base pt-4">
                      {t('financial_statements.liabilities')}
                    </td>
                  </tr>
                  <AccountRows rows={balanceSheet.liabilities} />
                  <StatementRow
                    label={t('financial_statements.total_liabilities')}
                    amount={balanceSheet.total_liabilities}
                    isTotal
                  />

                  {/* Equity */}
                  <tr>
                    <td colSpan={2} className="font-bold text-base pt-4">
                      {t('financial_statements.equity')}
                    </td>
                  </tr>
                  <AccountRows rows={balanceSheet.equity} />
                  <StatementRow
                    label={t('financial_statements.retained_earnings')}
                    amount={balanceSheet.retained_earnings}
                    indent
                  />
                  <StatementRow
                    label={t('financial_statements.current_year_earnings')}
                    amount={balanceSheet.current_year_earnings}
                    indent
                  />
                  <StatementRow
                    label={t('financial_statements.total_equity')}
                    amount={balanceSheet.total_equity}
                    isTotal
                  />

                  {/* Total L&E */}
                  <tr className="bg-base-200">
                    <td className="font-bold text-base py-3">
                      {t('financial_statements.liabilities_and_equity')}
                    </td>
                    <td className="text-right font-bold text-base tabular-nums py-3">
                      {formatBaht(balanceSheet.total_liabilities + balanceSheet.total_equity, { locale: 'en', decimals: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-base-content/60 text-sm text-center py-8">
              {t('financial_statements.empty_description')}
            </p>
          )}
        </Card>
      )}

      {/* Cash Flow */}
      {activeTab === 'cash_flow' && (
        <Card>
          {isLoadingCF ? (
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          ) : cashFlow ? (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <tbody>
                  {/* Operating */}
                  <tr>
                    <td colSpan={2} className="font-bold text-base pt-4">
                      {t('financial_statements.operating_activities')}
                    </td>
                  </tr>
                  <CashFlowRows items={cashFlow.operating} />
                  <StatementRow
                    label={`${t('financial_statements.subtotal')} — ${t('financial_statements.operating_activities')}`}
                    amount={cashFlow.total_operating}
                    isTotal
                  />

                  {/* Investing */}
                  <tr>
                    <td colSpan={2} className="font-bold text-base pt-4">
                      {t('financial_statements.investing_activities')}
                    </td>
                  </tr>
                  <CashFlowRows items={cashFlow.investing} />
                  <StatementRow
                    label={`${t('financial_statements.subtotal')} — ${t('financial_statements.investing_activities')}`}
                    amount={cashFlow.total_investing}
                    isTotal
                  />

                  {/* Financing */}
                  <tr>
                    <td colSpan={2} className="font-bold text-base pt-4">
                      {t('financial_statements.financing_activities')}
                    </td>
                  </tr>
                  <CashFlowRows items={cashFlow.financing} />
                  <StatementRow
                    label={`${t('financial_statements.subtotal')} — ${t('financial_statements.financing_activities')}`}
                    amount={cashFlow.total_financing}
                    isTotal
                  />

                  {/* Summary */}
                  <tr className="border-t">
                    <td className="text-sm py-2">{t('financial_statements.beginning_cash')}</td>
                    <td className="text-right text-sm tabular-nums py-2">{formatBaht(cashFlow.beginning_cash, { locale: 'en', decimals: 2 })}</td>
                  </tr>
                  <tr className="bg-base-200">
                    <td className="font-bold text-base py-3">
                      {t('financial_statements.net_change')}
                    </td>
                    <td className="text-right font-bold text-base tabular-nums py-3">
                      <span
                        className={cashFlow.net_change >= 0 ? 'text-success' : 'text-error'}
                      >
                        {formatBaht(cashFlow.net_change, { locale: 'en', decimals: 2 })}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="font-semibold py-2">{t('financial_statements.ending_cash')}</td>
                    <td className="text-right font-semibold tabular-nums py-2">{formatBaht(cashFlow.ending_cash, { locale: 'en', decimals: 2 })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-base-content/60 text-sm text-center py-8">
              {t('financial_statements.empty_description')}
            </p>
          )}
        </Card>
      )}
    </div>
  )
}
